import { useState, useRef, useEffect, useCallback } from "react";
import type { JSX } from "react";
import { cn } from "./cn";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";

/* ── Tab types ──────────────────────────────────────────────── */

export interface SearchableTab {
  readonly id: string;
  readonly label: string;
}

export interface SearchCategory {
  readonly id: string;
  readonly label: string;
  readonly icon?: IconName;
}

export interface SearchFilterButton {
  readonly id: string;
  readonly icon: IconName;
  readonly label?: string;
  readonly active?: boolean;
}

/* ── Component props ────────────────────────────────────────── */

export interface SearchableTabsProps {
  /** Tab definitions */
  readonly tabs: readonly SearchableTab[];
  /** Currently active tab id */
  readonly activeTab: string;
  /** Called when user selects a tab */
  readonly onTabChange: (tabId: string) => void;

  /** Enable the search feature (shows search icon) */
  readonly searchable?: boolean;
  /** Placeholder text for the search input */
  readonly searchPlaceholder?: string;
  /** Category filter pills shown in search mode */
  readonly searchCategories?: readonly SearchCategory[];
  /** Filter buttons (e.g. person, calendar) shown in the search toolbar */
  readonly searchFilters?: readonly SearchFilterButton[];
  /** Called when search query or active category changes */
  readonly onSearch?: (query: string, categoryId: string) => void;
  /** Number of search results to display */
  readonly searchResultCount?: number;
  /** Label for search results (e.g. "results for") */
  readonly searchResultLabel?: string;
  /** Called when user clicks prev/next in search navigation */
  readonly onSearchNavigate?: (direction: "prev" | "next") => void;
  /** Called when a filter button is toggled */
  readonly onFilterToggle?: (filterId: string) => void;

  /** Max visible tabs before showing "..." overflow button. No limit if omitted. */
  readonly maxVisibleTabs?: number;

  /** Extra className */
  readonly className?: string;
}

/**
 * A horizontal tab bar with an integrated search mode.
 *
 * Normal mode: shows tab buttons with an optional search icon on the right.
 * Search mode: replaces tabs with a search toolbar, nav arrows, filter icons,
 * and category filter pills.
 *
 * Generic — works for contacts, companies, projects, or any entity detail view.
 */
export function SearchableTabs({
  tabs,
  activeTab,
  onTabChange,
  searchable = false,
  searchPlaceholder = "Search…",
  searchCategories,
  searchFilters,
  onSearch,
  searchResultCount,
  searchResultLabel = "results for",
  onSearchNavigate,
  onFilterToggle,
  maxVisibleTabs,
  className,
}: SearchableTabsProps): JSX.Element {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>(
    searchCategories?.[0]?.id ?? "all",
  );
  const inputRef = useRef<HTMLInputElement>(null);
  // Hoisted before the early-return for search mode (line ~134).
  // Previously this useState lived after the conditional return,
  // which violated rules-of-hooks: toggling search open/closed
  // changed the number of hooks rendered → React threw
  // "Rendered fewer hooks than expected" the moment the user
  // clicked the search icon. (See SearchableTabs crash, May 2026.)
  const [overflowOpen, setOverflowOpen] = useState(false);

  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchOpen]);

  const handleSearchOpen = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const handleSearchClose = useCallback(() => {
    setIsSearchOpen(false);
    setSearchQuery("");
    setActiveCategory(searchCategories?.[0]?.id ?? "all");
  }, [searchCategories]);

  const handleQueryChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      onSearch?.(value, activeCategory);
    },
    [onSearch, activeCategory],
  );

  const handleCategoryChange = useCallback(
    (categoryId: string) => {
      setActiveCategory(categoryId);
      onSearch?.(searchQuery, categoryId);
    },
    [onSearch, searchQuery],
  );

  const handleClearQuery = useCallback(() => {
    setSearchQuery("");
    onSearch?.("", activeCategory);
    inputRef.current?.focus();
  }, [onSearch, activeCategory]);

  /* ── Search mode ──────────────────────────────────────────── */
  if (isSearchOpen) {
    return (
      <div className={cn("flex flex-col gap-3 px-5", className)}>
        {/* Search toolbar row */}
        <div className="flex items-center gap-2">
          {/* Nav arrows */}
          {onSearchNavigate ? (
            <div className="flex items-center gap-0.5 shrink-0">
              <button
                type="button"
                className="w-7 h-7 rounded-md border border-edge bg-transparent text-content-secondary
                           inline-flex items-center justify-center cursor-pointer
                           hover:bg-surface-hover hover:text-content transition-colors"
                onClick={() => { onSearchNavigate("prev"); }}
                aria-label="Previous result"
              >
                <Icon name="chevron-up" size={14} />
              </button>
              <button
                type="button"
                className="w-7 h-7 rounded-md border border-edge bg-transparent text-content-secondary
                           inline-flex items-center justify-center cursor-pointer
                           hover:bg-surface-hover hover:text-content transition-colors"
                onClick={() => { onSearchNavigate("next"); }}
                aria-label="Next result"
              >
                <Icon name="chevron-down" size={14} />
              </button>
            </div>
          ) : null}

          {/* Search input */}
          <div className="flex-1 flex items-center gap-2 bg-surface-secondary rounded-lg border border-edge px-3 h-9">
            <Icon name="search" size={14} className="text-content-tertiary shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => { handleQueryChange(e.target.value); }}
              placeholder={searchPlaceholder}
              className="flex-1 bg-transparent border-none outline-none text-sm text-content
                         placeholder:text-content-muted min-w-0"
            />
            {searchQuery ? (
              <button
                type="button"
                className="shrink-0 w-5 h-5 rounded-full bg-surface-hover inline-flex items-center justify-center
                           cursor-pointer text-content-tertiary hover:text-content transition-colors"
                onClick={handleClearQuery}
                aria-label="Clear search"
              >
                <Icon name="close" size={10} />
              </button>
            ) : null}
          </div>

          {/* Filter buttons */}
          {searchFilters?.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={cn(
                "w-8 h-8 rounded-lg border inline-flex items-center justify-center cursor-pointer transition-colors shrink-0",
                filter.active
                  ? "border-accent bg-accent-subtle text-accent"
                  : "border-edge bg-transparent text-content-secondary hover:bg-surface-hover hover:text-content",
              )}
              onClick={() => onFilterToggle?.(filter.id)}
              aria-label={filter.label ?? filter.id}
            >
              <Icon name={filter.icon} size={14} />
            </button>
          ))}

          {/* Close search */}
          <button
            type="button"
            className="w-8 h-8 rounded-lg border border-edge bg-transparent text-content-secondary
                       inline-flex items-center justify-center cursor-pointer shrink-0
                       hover:bg-surface-hover hover:text-content transition-colors"
            onClick={handleSearchClose}
            aria-label="Close search"
          >
            <Icon name="close" size={14} />
          </button>
        </div>

        {/* Category pills */}
        {searchCategories && searchCategories.length > 0 ? (
          <div className="flex items-center gap-2 overflow-x-auto">
            {searchCategories.map((category) => {
              const isActive = activeCategory === category.id;
              return (
                <button
                  key={category.id}
                  type="button"
                  className={cn(
                    "shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer border-none transition-colors",
                    isActive
                      ? "bg-accent text-on-bright"
                      : "bg-surface-secondary text-content-secondary hover:text-content hover:bg-surface-hover",
                  )}
                  onClick={() => { handleCategoryChange(category.id); }}
                >
                  {category.icon ? <Icon name={category.icon} size={12} /> : null}
                  {category.label}
                </button>
              );
            })}
          </div>
        ) : null}

        {/* Results summary */}
        {searchQuery && searchResultCount !== undefined ? (
          <div className="text-xs text-content-tertiary">
            {searchResultCount} {searchResultLabel} &ldquo;{searchQuery}&rdquo;
          </div>
        ) : null}
      </div>
    );
  }

  /* ── Normal tab mode ──────────────────────────────────────── */

  // Compute visible vs overflow tabs
  const needsOverflow = maxVisibleTabs !== undefined && tabs.length > maxVisibleTabs;
  let visibleTabs = tabs;
  let overflowTabs: readonly SearchableTab[] = [];

  if (needsOverflow) {
    const maxInline = maxVisibleTabs - 1; // Reserve 1 slot for "..."
    const activeInOverflow = tabs.slice(maxInline).some((t) => t.id === activeTab);

    if (activeInOverflow) {
      // Swap active tab into visible set
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const activeTabObj = tabs.find((t) => t.id === activeTab)!;
      const withoutActive = tabs.filter((t) => t.id !== activeTab);
      visibleTabs = [...withoutActive.slice(0, maxInline - 1), activeTabObj];
      overflowTabs = withoutActive.slice(maxInline - 1);
    } else {
      visibleTabs = tabs.slice(0, maxInline);
      overflowTabs = tabs.slice(maxInline);
    }
  }

  return (
    <div
      data-testid="entity-detail-tabs"
      className={cn("flex items-center h-[42px] px-5", className)}
      style={{ boxShadow: "inset 0 -1px 0 0 var(--color-edge)" }}
    >
      <div className="flex-1 flex gap-6 overflow-x-auto h-full min-w-0 scrollbar-none">
        {visibleTabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              data-tab-id={tab.id}
              type="button"
              className={cn(
                "bg-transparent border-none text-[11px] tracking-wide font-medium uppercase cursor-pointer whitespace-nowrap transition-colors h-full flex items-center shrink-0",
                isActive
                  ? "text-content"
                  : "text-content-tertiary hover:text-content-secondary",
              )}
              style={isActive ? { boxShadow: "inset 0 -1px 0 0 var(--color-blue)" } : undefined}
              onClick={() => { onTabChange(tab.id); }}
            >
              {tab.label}
            </button>
          );
        })}

        {needsOverflow ? (
          <div className="relative h-full flex items-center shrink-0">
            <button
              type="button"
              className={cn(
                "bg-transparent border-none text-[11px] tracking-wide font-medium uppercase cursor-pointer whitespace-nowrap transition-colors h-full flex items-center",
                overflowTabs.some((t) => t.id === activeTab)
                  ? "text-content"
                  : "text-content-tertiary hover:text-content-secondary",
              )}
              onClick={() => { setOverflowOpen((v) => !v); }}
            >
              ···
            </button>
            {overflowOpen ? (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => { setOverflowOpen(false); }}
                />
                <div className="absolute top-full left-0 z-50 mt-1 min-w-[140px] bg-surface border border-edge rounded-lg shadow-lg py-1">
                  {overflowTabs.map((tab) => (
                    <button
                      key={tab.id}
                      data-tab-id={tab.id}
                      type="button"
                      className={cn(
                        "w-full text-left px-3 py-1.5 text-[11px] tracking-wide uppercase font-medium cursor-pointer border-none transition-colors",
                        activeTab === tab.id
                          ? "bg-surface-secondary text-content"
                          : "bg-transparent text-content-secondary hover:bg-surface-hover hover:text-content",
                      )}
                      onClick={() => {
                        onTabChange(tab.id);
                        setOverflowOpen(false);
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      {searchable ? (
        <button
          type="button"
          className="shrink-0 w-8 h-8 rounded-lg bg-transparent border-none text-content-muted
                     inline-flex items-center justify-center cursor-pointer
                     hover:text-content hover:bg-surface-hover transition-colors ml-1"
          onClick={handleSearchOpen}
          aria-label="Search"
        >
          <Icon name="search" size={16} />
        </button>
      ) : null}
    </div>
  );
}
